import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './UpdateShareBanner.scss';
import { getTrackedUpdates, clearComponentUpdates, generateUpdateSummary } from '../../../utils/updateTracker';
import { toggleCreatePostModal } from '../../../Store/features/design/designSlice';
import { toast } from 'react-hot-toast';

/**
 * Component to show a banner prompting user to share their updates via post
 * @param {Object} props
 * @param {string} props.component - Component identifier (e.g., 'oneLink', 'community', 'companyProfile')
 * @param {function} props.onClose - Function to call when banner is closed
 * @param {function} props.setPopupOpen - Function to open CreatePostPopUp
 * @param {string} props.entityId - Optional ID of the entity (e.g., communityId)
 * @param {Object} props.readinessData - Optional readiness assessment data
 * @param {Object} props.tractionData - Optional traction data
 */
const UpdateShareBanner = ({ 
  component, 
  onClose, 
  setPopupOpen, 
  entityId = null, 
  readinessData = null,
  tractionData = null 
}) => {
  const [updates, setUpdates] = useState([]);
  const [isVisible, setIsVisible] = useState(true); 
  const dispatch = useDispatch();

  useEffect(() => {
    if (readinessData) {
      // Validate readiness data structure
      const hasValidData = ['startup', 'team', 'business'].some(type => 
        readinessData[type] && 
        typeof readinessData[type].readinessLevel === 'number' &&
        typeof readinessData[type].readinessDescription === 'string'
      );
      
      if (!hasValidData) {
        console.error('Invalid readiness data structure:', readinessData);
      }
    }

    if (tractionData) {
      if (!tractionData.update || !Array.isArray(tractionData.update)) {
        console.error('Invalid traction data structure:', tractionData);
      }
    }
    
    const componentUpdates = getTrackedUpdates().filter(update => update.component === component);
    setUpdates(componentUpdates);
    
    if (componentUpdates.length > 0) {
      setIsVisible(true);
    } else {
      setIsVisible(true);
    }
  }, [component, readinessData, tractionData]);

  const handleCreatePost = () => {
    // Generate summary text
    let summary = generateUpdateSummary(component);
    
    // Add readiness summary if available
    if (readinessData) {
      const readinessSummary = generateReadinessSummary(readinessData);
      summary = summary ? `${summary}\n\n${readinessSummary}` : readinessSummary;
    }

    // Add traction summary if available
    if (tractionData) {
      const tractionSummary = generateTractionSummary(tractionData);
      summary = summary ? `${summary}\n\n${tractionSummary}` : tractionSummary;
    }
 
    // Store in sessionStorage for CreatePostPopUp to access
    sessionStorage.setItem('update_summary', summary);
    
    // Store readiness data if available
    if (readinessData) {
      try {
        const readinessString = JSON.stringify(readinessData);
        sessionStorage.setItem('readiness_data', readinessString);
      } catch (error) {
        console.error('Error storing readiness data:', error);
      }
    }

    // Store traction data if available
    if (tractionData) {
      try {
        const tractionString = JSON.stringify(tractionData);
        sessionStorage.setItem('traction_data', tractionString);
      } catch (error) {
        console.error('Error storing traction data:', error);
      }
    }
    
    // Only store the community ID if it exists
    if (entityId) {
      sessionStorage.setItem('update_community_id', entityId);
    }
    
    // Clear the updates for this component
    clearComponentUpdates(component);
    
    // Close the banner
    setIsVisible(false);
    if (onClose) onClose();
    
    // Open the create post popup
    if (setPopupOpen) {
      setPopupOpen(true);
      dispatch(toggleCreatePostModal());
    } else {
      toast.success('Updates cleared. You can create a post to share your updates!');
    }
  };

  // Helper function to generate readiness summary
  const generateReadinessSummary = (data) => {
    const summaryParts = [];
    
    const categories = ['startup', 'team', 'business'];
    categories.forEach(category => {
      if (data[category]) {
        const { readinessLevel, readinessDescription } = data[category];
        if (readinessLevel && readinessDescription) {
          summaryParts.push(
            `🎯 ${category.charAt(0).toUpperCase() + category.slice(1)} Readiness Update:\n` +
            `Level: ${readinessLevel}/5\n` +
            `Status: ${readinessDescription}\n`
          );
        }
      }
    });
    
    if (summaryParts.length > 0) {
      return '📊 Readiness Assessment Updates:\n\n' + summaryParts.join('\n');
    }
    
    return '';
  };

  // Helper function to generate traction summary
  const generateTractionSummary = (data) => {
    if (!data || !data.update || data.update.length === 0) return '';

    const latestUpdate = data.update[data.update.length - 1];
    const previousUpdate = data.update.length > 1 ? data.update[data.update.length - 2] : null;

    let summary = '📈 Traction Metrics Update:\n\n';
    
    // Format date
    const date = new Date(latestUpdate.date);
    const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });
    
    summary += `Month: ${monthYear}\n`;
    summary += `Total Users: ${data.allUsers}\n`;
    summary += `Monthly Revenue: ${latestUpdate.revenue}\n`;
    summary += `New Users: ${latestUpdate.users}\n`;

    // Add growth metrics if we have previous data
    if (previousUpdate) {
      const revenueGrowth = ((latestUpdate.revenue - previousUpdate.revenue) / previousUpdate.revenue * 100).toFixed(1);
      const userGrowth = ((latestUpdate.users - previousUpdate.users) / previousUpdate.users * 100).toFixed(1);

      if (!isNaN(revenueGrowth) && revenueGrowth !== '0.0') {
        summary += `Revenue Growth: ${revenueGrowth}%\n`;
      }
      if (!isNaN(userGrowth) && userGrowth !== '0.0') {
        summary += `User Growth: ${userGrowth}%\n`;
      }
    }

    return summary;
  };

  const handleCancel = () => {
    // Clear the updates for this component
    clearComponentUpdates(component);
    
    // Close the banner
    setIsVisible(false);
    if (onClose) onClose();
  };

  return (
    <div className="update-share-banner">
      <div className="banner-content">
        <div className="update-icon">🔄</div>
        <div className="update-text">
          <h3>Share Your Updates</h3>
          <p>
            You've made {updates.length > 0 ? updates.length : 'several'} {updates.length === 1 ? 'change' : 'changes'} to your {component === 'oneLink' ? 'OneLink' : 
              component === 'community' ? 'Community' : 
              component === 'companyProfile' ? 'Company Profile' : 'profile'}.
            Would you like to share these updates with your network?
          </p>
        </div>
        <div className="banner-actions">
          <button className="yes-button" onClick={handleCreatePost}>
            Yes, Create Post
          </button>
          <button className="no-button" onClick={handleCancel}>
            No, Thanks
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateShareBanner; 